import React, { useEffect, useMemo, useState } from 'react';
import {
  listSection,
  updateRecord,
  resyncNewsletter,
  exportCsvUrl,
  SECTION_STATUSES,
  STATUS_LABELS,
} from '@/lib/adminApi';
import DetailDrawer from './DetailDrawer';

const SECTION_COLUMNS = {
  contacts: [
    { key: 'created_at', label: 'Date', render: (v) => (v ? new Date(v).toLocaleDateString() : '') },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'kind', label: 'Type', render: (v) => (v || '').toUpperCase() },
    { key: 'subject', label: 'Subject', render: (v) => v || '—' },
    { key: 'status', label: 'Status', render: (v) => STATUS_LABELS[v] || v },
  ],
  speaking: [
    { key: 'created_at', label: 'Date', render: (v) => (v ? new Date(v).toLocaleDateString() : '') },
    { key: 'organization', label: 'Organization' },
    { key: 'name', label: 'Contact' },
    { key: 'event_name', label: 'Event', render: (v) => v || '—' },
    { key: 'event_date', label: 'Event Date', render: (v) => v || '—' },
    { key: 'status', label: 'Status', render: (v) => STATUS_LABELS[v] || v },
  ],
  newsletter: [
    { key: 'created_at', label: 'Signup Date', render: (v) => (v ? new Date(v).toLocaleDateString() : '') },
    { key: 'email', label: 'Email' },
    { key: 'first_name', label: 'First Name', render: (v) => v || '—' },
    { key: 'last_name', label: 'Last Name', render: (v) => v || '—' },
    { key: 'source', label: 'Source', render: (v) => v || '—' },
    { key: 'resend_sync_status', label: 'Resend', render: (v) => v || 'pending' },
    { key: 'status', label: 'Status', render: (v) => STATUS_LABELS[v] || v },
  ],
  waitlist: [
    { key: 'created_at', label: 'Date', render: (v) => (v ? new Date(v).toLocaleDateString() : '') },
    { key: 'product', label: 'Product' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status', render: (v) => STATUS_LABELS[v] || v },
  ],
};

const SECTION_TITLES = {
  contacts: 'Contact Inquiries',
  speaking: 'Speaking Requests',
  newsletter: 'Newsletter Subscribers',
  waitlist: 'Waitlist',
};

export default function SubmissionsView({ section }) {
  const columns = SECTION_COLUMNS[section];
  const statuses = SECTION_STATUSES[section];

  const [filters, setFilters] = useState({ status: 'all', search: '', page: 1 });
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: 25 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  const params = useMemo(() => {
    const p = { page: filters.page, limit: 25, sort: 'newest' };
    if (filters.status && filters.status !== 'all') p.status = filters.status;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const d = await listSection(section, params);
      setData(d);
    } catch (err) {
      setError('Unable to load records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [section, params]);

  const onUpdate = async (id, payload) => {
    const updated = await updateRecord(section, id, payload);
    setData((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it.id === id ? { ...it, ...updated } : it)),
    }));
    if (selected && selected.id === id) setSelected({ ...selected, ...updated });
    return updated;
  };

  const onResync = async (id) => {
    const res = await resyncNewsletter(id);
    await load();
    return res;
  };

  const handleExport = () => {
    const url = exportCsvUrl(section, {
      ...(filters.status && filters.status !== 'all' ? { status: filters.status } : {}),
      ...(filters.search ? { search: filters.search } : {}),
    });
    // withCredentials via cookies works for GET when opening in same origin; open in new tab
    window.open(url, '_blank');
  };

  return (
    <div data-testid={`admin-${section}-view`}>
      <header className="mb-8">
        <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Section</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">
          {SECTION_TITLES[section]}
        </h1>
      </header>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-6">
        <input
          data-testid={`${section}-search-input`}
          placeholder="Search…"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
          className="flex-1 min-w-0 bg-white/80 border border-neutral-200 rounded-lg px-4 py-2.5 text-[14px] focus:outline-none focus:border-neutral-900"
        />

        <select
          data-testid={`${section}-status-filter`}
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
          className="bg-white/80 border border-neutral-200 rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:border-neutral-900"
        >
          <option value="all">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
          ))}
        </select>

        <button
          data-testid={`${section}-export-csv`}
          onClick={handleExport}
          className="bg-neutral-900 text-white uppercase tracking-[0.22em] text-[10px] px-5 py-3 rounded-full hover:bg-[#1a1a1a]"
        >
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-white/70 border border-neutral-200/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50/70">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200/60" data-testid={`${section}-tbody`}>
              {loading && (
                <tr><td colSpan={columns.length} className="px-5 py-10 text-center text-neutral-500 text-[12px] uppercase tracking-[0.2em]">Loading…</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={columns.length} className="px-5 py-10 text-center text-red-700">{error}</td></tr>
              )}
              {!loading && !error && data.items.length === 0 && (
                <tr><td colSpan={columns.length} className="px-5 py-14 text-center text-neutral-500">No records match your filters.</td></tr>
              )}
              {!loading && data.items.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelected(row)}
                  className="cursor-pointer hover:bg-neutral-50 transition-colors"
                  data-testid={`${section}-row`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className="px-5 py-4 text-neutral-800 align-top">
                      {c.render ? c.render(row[c.key]) : (row[c.key] || '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-6 text-[12px] text-neutral-500">
        <div>
          {data.total} record{data.total === 1 ? '' : 's'}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`${section}-prev-page`}
            disabled={data.page <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
            className="px-3 py-1.5 rounded border border-neutral-200 disabled:opacity-40"
          >
            ‹ Prev
          </button>
          <span>{data.page} / {data.pages || 1}</span>
          <button
            data-testid={`${section}-next-page`}
            disabled={data.page >= data.pages}
            onClick={() => setFilters((f) => ({ ...f, page: Math.min(data.pages, f.page + 1) }))}
            className="px-3 py-1.5 rounded border border-neutral-200 disabled:opacity-40"
          >
            Next ›
          </button>
        </div>
      </div>

      {selected && (
        <DetailDrawer
          section={section}
          record={selected}
          statuses={statuses}
          onClose={() => setSelected(null)}
          onUpdate={onUpdate}
          onResync={section === 'newsletter' ? onResync : null}
        />
      )}
    </div>
  );
}
